import type { QueryKey } from '@tanstack/react-query';

import { SESSION_GC_TIME } from '@/auth/session';
import { ApiProvider } from '@/provider/ApiProvider';
import { activeKey } from '@/query/keys';
import { queryClient } from '@/query/queryClient';
import { keyPart } from '@/query/useAccessQuery';

/**
 * The page shell's (Sidebar, Header, HeaderFilters, Footer) requests whose request never
 * changes, shared through the app's query client (slow pages U3). Every page renders its own
 * shell, so before, each navigation sent all of them again, and a component that asked twice
 * sent two requests.
 *
 * - Per session: the answer is kept for the active role assignment (keys `['a', <id>, 'shell',
 *   ...]`). A role switch drops it (clearSession) and reloads the page; sign-out and expiry
 *   clear the cache. Only a successful answer is kept: a refusal or failure is handed to the
 *   caller as before and asked again on the next page.
 * - Per page: the answer is kept for one navigation (`location.key`): every caller and
 *   remount of that page shares one request; the next navigation asks again, as before.
 *
 * The URLs, bodies and arguments are the ones the components sent before.
 */
export const SHELL = 'shell';

/** How long a per-page answer stays after the page's shell is gone (a remount reuses it). */
export const SHELL_PAGE_GC_TIME = 5 * 60 * 1000;

class NotKept extends Error {
  readonly answer: unknown;

  constructor(answer: unknown) {
    super('not kept');
    this.answer = answer;
  }
}

type Answer = { success?: boolean; [key: string]: any };

const sessionRequest = async <T extends Answer>(
  queryKey: QueryKey,
  // eslint-disable-next-line no-unused-vars
  request: (signal: AbortSignal) => Promise<T>,
): Promise<T> => {
  try {
    return await queryClient.fetchQuery({
      queryKey,
      queryFn: async ({ signal }) => {
        const answer = await request(signal);
        if (answer?.success !== true) throw new NotKept(answer);
        return answer;
      },
      staleTime: Infinity,
      gcTime: SESSION_GC_TIME,
      retry: false,
    });
  } catch (error) {
    if (error instanceof NotKept) return error.answer as T;
    throw error;
  }
};

const pageRequest = <T>(pageKey: string, queryKey: unknown[], request: () => Promise<T>) =>
  queryClient.fetchQuery({
    queryKey: [...queryKey, pageKey],
    queryFn: request,
    staleTime: Infinity,
    gcTime: SHELL_PAGE_GC_TIME,
    retry: false,
  });

/** HeaderFilters' country options (`Countries`, its header-filter body): once per session. */
export const fetchHeaderCountries = (body: FormData): Promise<Answer> =>
  sessionRequest(activeKey(SHELL, 'Countries', keyPart(body)), (signal) =>
    ApiProvider.webservicePostToken('Countries', body, 'yes', signal),
  );

/** Footer's `User/Menu` (it only shows the session-expired dialog on 'Unauthorized'): once per session. */
export const fetchUserMenu = (): Promise<Answer> =>
  sessionRequest(activeKey(SHELL, 'User/Menu'), () => ApiProvider.webserviceGet('User/Menu'));

/** BrandSelect's first page of the scope's brands (unsearched): once per page. */
export const fetchSwitcherBrands = (
  pageKey: string,
  params: Record<string, string>,
): Promise<Answer> =>
  pageRequest(pageKey, activeKey(SHELL, 'Brands/GetBrands', params) as unknown[], () =>
    ApiProvider.webserviceGet('Brands/GetBrands', params),
  );
